import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const MuscleBackfillPanel = ({ onClose }) => {
  const [job, setJob] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/exercises/muscle-backfill/status`, { headers: authHeaders() });
      setJob(r.data.job);
      setPending(r.data.pending);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load status');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (job?.status !== 'running') return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [job?.status, load]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/exercises/muscle-backfill/start`, {}, { headers: authHeaders() });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start');
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/exercises/muscle-backfill/stop`, {}, { headers: authHeaders() });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const running = job?.status === 'running';
  const pct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg">
      <Modal.Header onClose={onClose}>
        <h2 className="text-lg font-bold text-white" data-testid="muscle-backfill-title">AI Muscle Data</h2>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-blue-100/80 mb-4">
          Fills <b>target</b>, <b>synergist</b> and <b>stabilizer</b> muscles with AI so the body map lights
          up the full chain, not only your primary picks. Empty fields only — your curated data is never
          overwritten.
        </p>

        <div className="rounded-lg bg-black/30 border border-white/10 p-3 text-sm space-y-1" data-testid="muscle-backfill-stats">
          <div className="flex justify-between text-blue-100">
            <span>Exercises still missing muscle data</span>
            <span className="font-semibold tabular-nums" data-testid="muscle-backfill-pending">{pending ?? '…'}</span>
          </div>
          {job && (
            <>
              <div className="flex justify-between text-blue-100">
                <span>Status</span>
                <span className="font-semibold" data-testid="muscle-backfill-status">{job.status}</span>
              </div>
              <div className="flex justify-between text-blue-100">
                <span>Processed / total</span>
                <span className="tabular-nums">{job.processed} / {job.total}</span>
              </div>
              <div className="flex justify-between text-blue-100">
                <span>Updated</span>
                <span className="tabular-nums text-green-300">{job.updated}</span>
              </div>
              {job.failed > 0 && (
                <div className="flex justify-between text-red-300">
                  <span>Failed</span>
                  <span className="tabular-nums">{job.failed}</span>
                </div>
              )}
              {job.error && <div className="text-red-300 text-xs">{job.error}</div>}
            </>
          )}
        </div>

        {job?.total > 0 && (
          <div className="mt-3 h-2 rounded bg-gray-700 overflow-hidden" data-testid="muscle-backfill-progress">
            <div className="h-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-300" data-testid="muscle-backfill-error">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          {running ? (
            <button
              onClick={stop}
              disabled={busy}
              className="px-4 py-2 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
              data-testid="muscle-backfill-stop-btn"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={start}
              disabled={busy || pending === 0}
              className="px-4 py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 transition-colors disabled:opacity-50"
              data-testid="muscle-backfill-start-btn"
            >
              {busy ? 'Starting…' : 'Start backfill'}
            </button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MuscleBackfillPanel;
